// pages/batches/index.tsx
import { BatchActionType, BatchPermissions, createBatchColumns } from "@/colDef/batchColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import BatchDialog from "@/components/formDialog/BatchDialog";
import Search from "@/components/Search";
import AsyncAutocompleteSingleSelect from "@/components/Autocomplete/AsyncAutocompleteSingleSelect";

import {
  useBatchesQuery,
  useDeleteBatchMutation,
} from "@/hooks/api/batch.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  BatchParamsType,
  BatchType,
} from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import { useAuthStore } from "@/store";
import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const BatchesPage = () => {
  const user = useAuthStore((state) => state.user);
  const permissions: BatchPermissions = {
    canUpdate: !!user?.permissions?.includes("batch.update"),
    canDelete: !!user?.permissions?.includes("batch.delete"),
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [deptId, setDeptId] = useState<string | null>(null);

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });

  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const batchDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    batchId: "",
    batchData: null as BatchType | null,
  });

  const filters: BatchParamsType = useMemo(
    () => ({
      limit,
      page,
      sortBy: "createdAt",
      sortOrder: "desc",
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(deptId && { dept_id: deptId }),
    }),
    [debouncedSearch, deptId, page, limit]
  );
  const { data, isLoading, isError, refetch } = useBatchesQuery(filters);

  const batches = data?.batches ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const deleteMutation = useDeleteBatchMutation(filters);

  const handleDelete = useCallback(() => {
    const id = deleteDialog.state.mantraId;
    if (!id) return;
    deleteMutation.mutate(id, { onSuccess: deleteDialog.close });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: BatchActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete batch{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const batchData = batches.find((b) => b.id === payload.id) ?? null;
        batchDialog.open({ mode: "edit", batchId: payload.id, batchData });
      }
    },
    [deleteDialog, batchDialog, batches]
  );

  const handleCreateClick = () => {
    batchDialog.open({ mode: "create", batchId: "", batchData: null });
  };

  const columns = useMemo(
    () => createBatchColumns(handleActionClick, permissions),
    [handleActionClick, permissions]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load batches."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Batch Management</h1>
        <Button
          onClick={handleCreateClick}
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        {/* Department filter */}
        <div className="w-56">
          <AsyncAutocompleteSingleSelect
            placeholder="All Departments"
            fetchOptions={async ({ search, limit }) => {
              const res = await departmentService.fetchDepartmentOptions({ search, page: 1, pageSize: limit });
              return res.items.map((d) => ({ value: d.value, label: d.label }));
            }}
            queryKey={["departmentsDrop"]}
            value={deptId}
            onValueChange={(val: string | null) => {
              setDeptId(val);
              resetPage();
            }}
            showClear
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<BatchType, any>
              columns={columns}
              data={batches}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={batches.length}
        onPageChange={setPage}
      />

      {/* Batch Create / Edit Dialog */}
      <BatchDialog
        mode={batchDialog.state.mode}
        isOpen={batchDialog.state.isOpen}
        onOpenChange={(open) => !open && batchDialog.close()}
        filters={filters}
        batchId={batchDialog.state.batchId}
        batchData={batchDialog.state.batchData}
        onCancel={batchDialog.close}
      />

      {/* Delete Confirmation Dialog */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default BatchesPage;
